import React from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Typography, Box, Tooltip, Chip,
  IconButton
} from "@mui/material";
import ReceiptIcon from '@mui/icons-material/Receipt';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { formatMoney, formatDate } from "./ProducerDashboardHelper";
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { ProducerDashboardInvoice } from "../interfaces/ProducerDashboard";
import OneCommTransactionsModal from "./onecomm-transactions/OneCommTransactionsModal";

interface PayableInvoicesProps {
  invoices: ProducerDashboardInvoice[];
  currency: string;
}

export default function PayableInvoices({ invoices, currency }: PayableInvoicesProps) {

  const [modalOpen, setModalOpen] = React.useState(false);

  const [selectedInvoice, setSelectedInvoice] = React.useState<ProducerDashboardInvoice | null>(null);

  const handleOpenModal = (invoice: ProducerDashboardInvoice) => {
    setSelectedInvoice(invoice);
    setModalOpen(true);
  };

  return (
    <Box sx={{ mt: 4, mb: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 2, display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
        <ReceiptIcon fontSize="small" color="primary" /> Payable Invoices
      </Typography>

      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, border: '1px solid #e0e0e0' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ "& th": { bgcolor: '#f8f9fa', fontWeight: 800, fontSize: '0.7rem', textTransform: 'uppercase' } }}>
              <TableCell sx={{ width: '35%' }}>Invoice ID & Error Log</TableCell>
              <TableCell>Edition Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Payout Status</TableCell>
              <TableCell align="center">Pending Transfers</TableCell>
              <TableCell align="right">Producer Part</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {invoices.length > 0
              ? invoices.map((invoice) => {
                const logText = invoice.payout_error_log || "";

                const pendingCount = invoice.total_pending_transactions?.total_count || 0;

                return (
                  <TableRow
                    key={invoice.id}
                    sx={{
                      '&:last-child td, &:last-child th': { border: 0 },
                      '&:hover': { bgcolor: '#fcfcfc' },
                      verticalAlign: 'top'
                    }}
                  >
                    <TableCell sx={{ py: 1.5 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: logText ? 0.5 : 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
                            {invoice.id}
                          </Typography>
                          {logText && <ErrorOutlineIcon sx={{ fontSize: 14, color: 'text.disabled' }} />}
                        </Box>

                        {logText && (
                          <Tooltip
                            title={logText}
                            arrow
                            placement="right"
                            componentsProps={{ tooltip: { sx: { maxWidth: 500, bgcolor: '#424242' } } }}
                          >
                            <Box sx={{
                              bgcolor: '#f8f8f8',
                              borderLeft: '2px solid #bdbdbd',
                              p: 0.5,
                              borderRadius: '0 4px 4px 0',
                              maxWidth: 'fit-content'
                            }}>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'text.secondary',
                                  fontSize: '0.55rem',
                                  fontWeight: 400,
                                  fontFamily: 'monospace',
                                  lineHeight: 1,
                                  cursor: 'help',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {logText.length > 35
                                  ? `${logText.substring(0, 35)}...`
                                  : logText}
                              </Typography>
                            </Box>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>

                    <TableCell sx={{ fontSize: '0.75rem', py: 1.5 }}>
                      {formatDate(invoice.edition_date)}
                    </TableCell>

                    <TableCell sx={{ py: 1.5 }}>
                      <Chip
                        label={invoice.status}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.65rem', height: 20, fontWeight: 600, color: 'text.secondary' }}
                      />
                    </TableCell>

                    <TableCell sx={{ py: 1.5 }}>
                      <Chip
                        label={invoice.payout_status.replace(/_/g, ' ')}
                        size="small"
                        sx={{ fontSize: '0.65rem', height: 20, fontWeight: 700, bgcolor: '#e8eaed' }}
                      />
                    </TableCell>

                    <TableCell align="center" sx={{ py: 1.5 }}>
                      {pendingCount > 0 && (
                        <Tooltip title={`${pendingCount} pending movements to settle`}>
                          <IconButton size="small" color="warning" onClick={() => handleOpenModal(invoice)}>
                            <WarningAmberIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>

                    <TableCell align="right" sx={{ py: 1.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 900, fontSize: '0.85rem' }}>
                        {formatMoney(invoice.total_producer_amount, currency)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )
              })
              : <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.disabled', fontStyle: 'italic', fontSize: '0.8rem' }}>
                  No payable invoices found.
                </TableCell>
              </TableRow>
            }
          </TableBody>
        </Table>
      </TableContainer>

      {selectedInvoice && (
        <OneCommTransactionsModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          data={selectedInvoice.total_pending_transactions}
          currency={currency}
          title={`Invoice ${selectedInvoice.id} Details`}
        />
      )}
    </Box>
  );
}